// Details on one trip. The id comes from the URL; the trip (and its item) may
// also arrive in router state from the Upcoming Trips card so the page can
// skip a refetch. A deep link or reload loads both from the database.
//
// Mirrors the logbook-entry detail page (LogbookEntry.jsx): every field is
// directly editable here -- and autosaves. Typed edits persist ~1.5s after
// the last keystroke or on leaving the field; date picks persist
// immediately; map/route edits ride the same debounce (drags are
// rapid-fire). There is no Save button; the global SaveStatusPill reports
// progress and errors. Trips are a fixed, fully column-backed shape, so this
// is simpler -- no logbook-type field registry.
//
// A named leg also offers "Add Next Leg" beside Delete: it creates the leg that
// follows this one (nextLegDefaults, shared with the group page's Add leg) and
// opens it here, re-dating the legs behind it along the days chain.
//
// Distance is the one field the user doesn't type: while the route measures
// (two or more points on the map) it's read-only and rewritten from the route
// on every map edit -- see routeDistanceValue below.
//
// The route lives inside the trip's reserved `fields` JSON blob
// (TripRoute.mjs owns that contract) and is tracked by a generation counter:
// every route edit bumps `routeGen`, each save payload records which
// generation it carried, and only a save that carried the latest generation
// marks the route clean -- so a save completing mid-drag can't swallow newer
// route edits.

import { useState, useEffect, useCallback, useMemo, useRef, Suspense } from 'react';
import { lazyWithReload } from '../../utilities/lazyWithReload';
import { useParams, useLocation } from 'react-router-dom';
import { useRootCrumbNavigate } from '../item/RootCrumb';
import Button from 'react-bootstrap/Button';
import { DataCard, CardHeader, Confirm, Field, buildEditedFields, useStatusMessage, Spinner } from '../../components';
import { useAutosave } from '../../utilities/useAutosave';
import { saveQueue, testHooksAllowed } from '../../utilities/SaveQueue.mjs';
import { ItemBreadcrumbs } from '../item/ItemBreadcrumbs';
import * as Database from '../../utilities/Database.mjs';
import {
  urlForTrips,
  urlForTrip,
  urlForTripGroup,
  urlForPlace,
  urlForPlaceVisits,
} from '../../fleet-shared/Urls.mjs';
import { tripFields, tripTitle, tripDateMs, legDays, validateTripLeg } from '../../fleet-shared/TripFields.mjs';
import { useCopyLegToLogbook } from './useCopyLegToLogbook';
import {
  tripGroupKey,
  isNamedGroupKey,
  cascadeLegDates,
  cascadeAfterDateChange,
  nextLegDefaults,
} from '../../fleet-shared/TripGroups.mjs';
import {
  legLane,
  loadGroupLegs,
  persistLegDates,
  runLegCascade,
  settledLegCascades,
} from '../../utilities/TripSchedule.mjs';
import {
  routeFromTrip,
  mergeRouteIntoFields,
  backfillEndpointNames,
  routeDistanceNm,
  resolveRouteAgainstPlaces,
  anchorName,
  tripWithAnchorNames,
} from '../../fleet-shared/TripRoute.mjs';
import { loadPlaceIndex, ensurePlaces, placesFailedNotice } from '../../utilities/places.mjs';
import { serializeTrip, parseTripExport, legToUpdateInput, suggestedTripFileName } from '../../fleet-shared/TripIO.mjs';
import { downloadJson, readFileAsText } from '../../utilities/downloadFile.mjs';
import { useUnsavedChangesGuard } from '../../utilities/useUnsavedChangesGuard';
import { removeTripWeatherSubscriptionsForGroup } from '../../utilities/tripWeatherEmail.mjs';
import { removeTripShareForGroup } from '../../utilities/shareTrip.mjs';
import TripWaypoints from './TripWaypoints';
import { useRouteLocator } from './useRouteLocator';
import { tripSuggestionsFromLegs, withTripSuggestions } from '../../utilities/tripSuggestions';

// Lazy: TripRouteMap is the only import path that pulls in maplibre-gl
// (~250 KB gz); keep it out of the entry bundle.
const TripRouteMap = lazyWithReload(() => import('../../components/map/TripRouteMap'), 'TripRouteMap');

// From/To get no inputs of their own here: the waypoint list's Start and End
// rows edit those columns (see TripWaypoints), so the field list is split
// around it -- Trip Name, Date, [Waypoints], Days, Distance, Notes.
// `tripFields` itself is untouched: the autosave payload and the legs table
// still need the full list.
//
// Days sits BELOW the waypoints, with Distance: on screen it reads as part of
// the "how long / how far" pair that describes the route just above it, rather
// than pushing the waypoint list a row further down the page.
const ENDPOINT_KEYS = ['from', 'to'];
const HEAD_KEYS = ['name', 'date'];

// Said permanently beside the Days input rather than in a tooltip: editing it
// rewrites the dates of every later leg of the trip (see cascadeLegDates), and
// a control that quietly changes rows you can't see needs to say so where a
// touch or keyboard user will meet it.
const DAYS_NOTE = 'Days you stay here. Later legs shift to follow.';
const headFields = tripFields.filter((field) => HEAD_KEYS.includes(field.key));
const tailFields = tripFields.filter(
  (field) => !HEAD_KEYS.includes(field.key) && !ENDPOINT_KEYS.includes(field.key)
);

// The route as the page edits it: anchors that were never named fall back to
// the trip's From/To so the Start/End rows aren't blank beside a column that
// has a value.
function routeDraftFor(trip) {
  return backfillEndpointNames(routeFromTrip(trip), trip || {});
}

// The trip's Distance as the route measures it: great-circle length in
// nautical miles to one decimal, or null when the route can't produce one
// (fewer than two points on the map). Distance is derived here, not typed --
// there's no "use this number" button to forget to press -- so a route that
// stops measuring blanks the field rather than leaving a figure that no longer
// describes anything.
function routeDistanceValue(route) {
  const nm = routeDistanceNm(route);
  return nm == null ? null : Math.round(nm * 10) / 10;
}

const Trip = () => {
  const { itemId, tripId } = useParams();
  const location = useLocation();
  const navigate = useRootCrumbNavigate();

  const [StatusMessage, { showText, showError, showLoading, hideStatus }] = useStatusMessage();

  const [item, setItem] = useState(null);
  const [trip, setTrip] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);

  // Copy this leg into the Captain's Log. The hook re-reads the leg from the
  // server after flushing this page's autosave, so it is handed the id and
  // nothing else matters about what it is passed.
  const [copyToLogModals, { copyLegToLogbook, copying }] = useCopyLegToLogbook({
    item,
    itemId,
    showText,
    showError,
    hideStatus,
  });

  // Import flow: a validated single leg from a file, awaiting the
  // replace-this-leg confirmation.
  const fileInputRef = useRef(null);
  const [pendingReplace, setPendingReplace] = useState(null);

  // Import runs through the queue but disables the Import/Export buttons
  // while it's replacing the leg.
  const [importing, setImporting] = useState(false);

  // Add Next Leg is in flight: it saves, reads the trip's other legs, creates,
  // re-dates and navigates, and a second click part-way through would create a
  // second leg off the same stale read.
  const [addingLeg, setAddingLeg] = useState(false);

  // Map route edits. The draft is what the map and waypoint list render;
  // saves pick it up via the refs below (payloads are built when the save
  // RUNS, so they always carry the newest draft).
  const [routeDraft, setRouteDraft] = useState(null);
  const routeDraftRef = useRef(null);
  const routeGen = useRef(0); // bumped on every route edit
  const persistedRouteGen = useRef(0); // the generation the server has
  const lastBuiltRouteGen = useRef(0); // the generation the in-flight payload carries

  // The latest server copy of the trip, for payload identity keys and the
  // `fields` blob merge -- kept in a ref so a queued save that runs between
  // renders still sees the newest copy.
  const tripRef = useRef(null);

  const loadedTripId = useRef(undefined);

  // The shared gazetteer, as { byId, byKey }. A place row OWNS the position of
  // every waypoint bound to it -- moving a place in /me/places moves the
  // waypoint -- and the lat/lon in the route blob is the last-known snapshot
  // that keeps the map drawable when this never arrives.
  //
  // In a ref for the resolve and the locator's synchronous curated check, and
  // ALSO in state, because the map card now draws every row as a layer and the
  // dots have to appear when the index lands. Deliberately the SAME read, set
  // in one `.then` -- not a second usePlaceIndex() beside it: two independent
  // reads on one page can straddle the module cache's expiry and spell one
  // place two ways, which is why TripsCalendarSection takes its index as a prop
  // instead of mounting its own hook.
  const placeIndexRef = useRef(null);
  const [placeIndex, setPlaceIndex] = useState(null);

  // Resolve the current draft against the gazetteer once the index lands.
  //
  // Seeding only: if the user has already touched the route this session
  // (routeGen > 0) the index is a snapshot from BEFORE their edit, and a drag
  // writes back to the row fire-and-forget -- so resolving now would snap the
  // point they just moved back to where the row was when the page opened.
  // Skipping costs nothing; the next page load resolves.
  useEffect(() => {
    let mounted = true;
    loadPlaceIndex().then((index) => {
      if (!mounted) return;
      placeIndexRef.current = index;
      // Before the seeding guard below: the layer draws whether or not the
      // resolve has anything to say.
      setPlaceIndex(index);
      if (routeGen.current !== 0) return;
      const base = routeDraftRef.current;
      const resolved = resolveRouteAgainstPlaces(base, index);
      if (resolved === base) return;
      setRouteDraft(resolved);
      routeDraftRef.current = resolved;
      routeGen.current = 1;
    });
    return () => {
      mounted = false;
    };
  }, []);

  // Test hook: a cascade is invisible to the save queue, so awaitAutosaved()
  // cannot be what an e2e spec waits on here. It is kicked off from onSaved --
  // which runs as the lane goes idle, so the queue reports drained either way
  // -- and persistLegDates writes through the database directly rather than
  // queueing a job, so pendingCount() would never count it even if the timing
  // were kinder. So expose the cascade chain and let a spec await it.
  //
  // Reading it at call time is the point: the chain is serialized module-wide,
  // so the promise it hands back subsumes every cascade queued ahead of it.
  useEffect(() => {
    if (!testHooksAllowed()) return undefined;
    window.__taskPogoTripCascade = { settled: () => settledLegCascades() };
    return () => {
      delete window.__taskPogoTripCascade;
    };
  }, []);

  // Focus the first field (Trip Name) once the trip loads. Plain inputs honour
  // `autoFocus`/`setFocus`, but focusing by hand also covers the resync case
  // where the same page swaps to a different trip without remounting.
  const firstFieldRef = useRef(null);
  const focusedTripId = useRef(undefined);

  // This trip's autosave lane. Shared with every other writer against the
  // same leg (the group page's re-date cascade), which is why the load below
  // has to consult it before trusting a handover -- and why TripSchedule
  // waits on the same lanes before cascading off a sibling's date.
  const recordKey = legLane(tripId);

  // Load item (for the breadcrumbs) and trip. Prefer whatever the previous
  // page handed over in router state; fall back to the database.
  useEffect(() => {
    let mounted = true;
    // Lets the lane wait below be dropped on unmount instead of holding a
    // queue subscription until (or unless) the write finishes.
    const leaving = new AbortController();
    async function load() {
      try {
        showLoading();

        // Read the lane BEFORE any await. The handover was captured when the
        // previous page navigated, so "is it stale?" is a question about that
        // moment -- and the item fetch below is a round trip long enough for
        // the write to land, which would make a stale snapshot look current.
        const handoverPending = saveQueue.isPending(recordKey);

        let loadedItem = location.state?.item?.id === itemId ? location.state.item : null;
        if (!loadedItem) {
          loadedItem = await Database.retrieveItem(itemId);
        }
        if (!mounted) return;
        setItem(loadedItem);

        let loadedTrip = location.state?.trip?.id === tripId ? location.state.trip : null;

        // Consume the handover ONCE, then strip it from history state (the
        // same pattern useSharedItemPageState uses): router state survives
        // reloads, and under autosave the handed-over copy goes stale the
        // moment the first edit lands -- a reload must come back from the
        // database, not resurrect pre-save values.
        if (loadedTrip || location.state?.item) {
          const newState = { ...location.state };
          delete newState.trip;
          delete newState.item;
          window.history.replaceState(newState, document.title);
        }

        // The page that handed this trip over autosaves too, so its snapshot
        // can predate a write that is still in flight. Worse here than on the
        // group page: a handover means we never fetch, so those pre-save
        // values would stick -- and become the baseline this page's own edits
        // diff against, quietly reverting the edit that was in flight. Drop
        // the handover and read once the lane drains.
        if (loadedTrip && handoverPending) {
          loadedTrip = null;
        }

        if (!loadedTrip) {
          await saveQueue.settled(recordKey, { signal: leaving.signal });
          if (!mounted) return;
          loadedTrip = await Database.retrieveTrip(tripId);
          if (loadedTrip == null) { throw new Error('Trip not found'); }
        }
        if (!mounted) return;
        setTrip(loadedTrip);

        hideStatus();
      } catch (err) {
        if (mounted) showError(err);
      }
    }
    load();
    return () => { mounted = false; leaving.abort(); };
    // location.state is read once on mount; re-running on itemId/tripId change
    // is enough (a new trip means a new URL).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemId, tripId]);

  // A new (or first) trip loaded -- (re)seed the route draft (the autosave
  // hook below reseeds the form itself, keyed on the same id change).
  //
  // The seed is resolved against the gazetteer when the index has already
  // arrived; otherwise the effect below does it when it does. A resolve that
  // MOVED something starts the route one generation ahead of the server, which
  // is the whole lazy-write-back mechanism: no save is scheduled here (that
  // takes a setFormState, which this deliberately doesn't call), but the next
  // save the user causes for any reason carries the corrected route with it.
  if (trip && trip.id !== loadedTripId.current) {
    loadedTripId.current = trip.id;
    tripRef.current = trip;
    const base = routeDraftFor(trip);
    const draft = placeIndexRef.current
      ? resolveRouteAgainstPlaces(base, placeIndexRef.current)
      : base;
    setRouteDraft(draft);
    routeDraftRef.current = draft;
    routeGen.current = draft === base ? 0 : 1;
    persistedRouteGen.current = 0;
    lastBuiltRouteGen.current = 0;
  }

  // Stable, so the map card's PlacesLayer memo holds: a fresh arrow every
  // render would re-render the layer on every weather tick, and it
  // re-stringifies the whole gazetteer when it does.
  const handleEditPlace = useCallback((placeId) => navigate(urlForPlace(placeId)), [navigate]);
  const handlePlaceVisits = useCallback(
    (placeId) => navigate(urlForPlaceVisits(placeId)),
    [navigate]
  );

  // Autosave. buildPayload folds in this page's special rules: id/itemId
  // always ride along, an emptied `date` falls back to the trip's previous
  // one (it's a required index sort key and must never go null), and when
  // the route has unsaved edits the payload carries the trip's `fields`
  // blob with the latest route merged in (mergeRouteIntoFields keeps any
  // other keys in the blob intact -- AWSJSON replaces the whole attribute).
  const {
    formState,
    setFormState,
    fieldErrors,
    flush,
    applyExternalSave,
    guardDirty,
  } = useAutosave({
    recordKey,
    original: trip,
    fields: tripFields,
    buildPayload: (state, baseline) => {
      const current = tripRef.current || trip;
      const payload = buildEditedFields(tripFields, state, baseline || current);
      payload.id = current.id;
      payload.itemId = current.itemId;
      if (!payload.date) {
        payload.date = current.date;
      }
      if (routeGen.current > persistedRouteGen.current) {
        const route = routeDraftRef.current;
        payload.fields = mergeRouteIntoFields(current.fields, route);

        // handleRouteChange mirrors `distance` and the From/To columns off the
        // route on every EDIT, but a gazetteer resolve moves points without
        // going through it (deliberately -- that call is what would schedule a
        // save on page load). So the mirror is re-derived here, or a resolved
        // route would ship beside a distance the route no longer measures and
        // a From column naming a place the gazetteer has since renamed.
        //
        // Re-deriving can't fight a user edit, because for both of these the
        // ROUTE is the source of truth: Distance renders read-only while the
        // route measures, and an anchor that exists as a waypoint is renamed
        // through the waypoint (onEndpointChange only fires for an anchor that
        // ISN'T in the route, which is exactly when anchorName returns
        // undefined and this skips). This is the same rule handleRouteChange
        // applies, just reached from the other direction.
        //
        // The null case is deliberately NOT mirrored: handleRouteChange blanks
        // Distance when a user's edit drops the route below two placed points
        // (and buildEditedFields sends that as a real null), but a passive
        // resolve must never blank a hand-typed distance on a route that was
        // never measuring in the first place.
        const distance = routeDistanceValue(route);
        if (distance != null) payload.distance = distance;
        ENDPOINT_KEYS.forEach((kind) => {
          const name = anchorName(route, kind);
          if (name !== undefined) payload[kind] = name;
        });
      }
      lastBuiltRouteGen.current = routeGen.current;
      return payload;
    },
    save: (payload) => Database.updateTrip(payload),
    validate: validateTripLeg,
    onSaved: (saved, payload) => {
      if (!saved) return;
      // Read the pre-save row BEFORE tripRef moves on: it's the only record of
      // what `date` and `days` used to be, and the cascade needs both to tell a
      // real change from a payload that merely re-sent them.
      const previous = tripRef.current;
      // Only a save that carried the latest route generation marks the
      // route persisted -- newer drag edits stay pending. (The handed-over
      // router state was already stripped on load, so a reload refetches
      // from the database -- no need to keep history state fresh per save.)
      persistedRouteGen.current = Math.max(persistedRouteGen.current, lastBuiltRouteGen.current);
      tripRef.current = saved;
      setTrip(saved);
      cascadeSiblings(saved, payload, previous);
    },
  });

  // Put the cursor in the first field once, per trip.
  useEffect(() => {
    if (!trip || focusedTripId.current === trip.id) return;
    const node = firstFieldRef.current;
    if (!node) return;
    focusedTripId.current = trip.id;
    // react-datepicker exposes setFocus(); plain inputs just focus().
    if (typeof node.setFocus === 'function') {
      node.setFocus();
    } else if (typeof node.focus === 'function') {
      node.focus();
    }
  }, [trip]);

  // What this leg is CALLED, for the heading and the export file name. The
  // from/to columns are a mirror of the route written at save time, so a place
  // renamed in /me/places since then is reachable only through the waypoint's
  // placeId -- and `routeDraft` is where that lives, already resolved against
  // the gazetteer (and already carrying unsaved renames, so the heading tracks
  // the waypoint boxes as they are typed). Display only: `trip` and every
  // payload built from it stay untouched, and the correction still rides out on
  // the next real save via buildPayload.
  const displayTrip = useMemo(() => tripWithAnchorNames(trip, routeDraft), [trip, routeDraft]);

  // Warn before navigating away only when an edit is held invalid or a save
  // failed -- valid pending edits flush on the way out.
  const { promptOpen: showLeavePrompt, confirmLeave, cancelLeave, markClean } = useUnsavedChangesGuard(guardDirty);

  const updateFormState = setFormState;

  // The item's other trips, for the Trip Name box's menu. Read lazily -- the
  // input calls this once, on first focus -- because most visits to this page
  // never touch that field, and this is a full read of the item's legs.
  const loadTripSuggestions = useCallback(
    () => Database.listTripsForItem(itemId).then(tripSuggestionsFromLegs),
    [itemId]
  );
  const namedHeadFields = useMemo(
    () => withTripSuggestions(headFields, loadTripSuggestions),
    [loadTripSuggestions]
  );

  // Route edits from either editor -- the waypoint list in the controls
  // above, or the map below -- land here and autosave like any field
  // (debounced: drags are rapid-fire).
  //
  // An anchor's name and the trip's `from`/`to` column are one value in two
  // places, so a rename has to update both. That mirroring lives HERE rather
  // than in each editor: the waypoint list, the map's marker modal and a
  // marker drag all funnel through this one function, and an editor that
  // forgot the column would silently reintroduce the divergence this page's
  // single From/To editor exists to remove. The setFormState call (made even
  // when no column changed) is also what schedules the save; buildPayload
  // picks the route up from the refs when the save runs.
  function handleRouteChange(nextRoute) {
    setRouteDraft(nextRoute);
    routeDraftRef.current = nextRoute;
    routeGen.current += 1;

    const renamed = {};
    ENDPOINT_KEYS.forEach((kind) => {
      const name = anchorName(nextRoute, kind);
      // `|| ''` so a null column and an unnamed anchor aren't seen as a
      // change -- that would dirty the form on an unrelated route edit.
      if (name !== undefined && name !== (formState[kind] || '')) {
        renamed[kind] = name;
      }
    });
    // Distance follows the route: every edit that changes the shape of it --
    // a drag, an inserted waypoint, a point dropping off the map -- rewrites
    // the field. A null blanks it, and buildEditedFields sends that as an
    // explicit null so the column really clears.
    updateFormState({ ...formState, ...renamed, distance: routeDistanceValue(nextRoute) });
  }

  // A Start/End row for an anchor that isn't on the map yet: there's no
  // waypoint to rename, so the column is edited on its own.
  function handleEndpointChange(kind, name) {
    updateFormState({ ...formState, [kind]: name });
  }

  // Does the route yield a length? While it does, Distance is its readout
  // rather than an input.
  const routeMeasures = routeDistanceValue(routeDraft) != null;

  // The notes under the fields that follow the waypoint list.
  function tailNote(key) {
    if (key === 'days') return DAYS_NOTE;
    if (key === 'distance' && routeMeasures) return 'Measured along the route below.';
    return undefined;
  }

  // Geocoding and tap-to-place, owned here because BOTH editors offer them:
  // every row in the waypoint list has "Lookup place" / "Place on map", and the map
  // card has its empty state and half-placed prompt. One owner means one
  // queue of points waiting for a tap. It reads the route through the same
  // ref the save payloads do, so a geocode that lands mid-drag merges onto
  // the newest route rather than the one it started from.
  const locator = useRouteLocator({
    route: routeDraft,
    routeRef: routeDraftRef,
    onRouteChange: handleRouteChange,
    endpointName: (kind) => (formState ? formState[kind] : '') || '',
    // The same index the overlay resolves against, so the locator can answer
    // "is this point bound to a curated row?" without a round trip. A ref, not
    // a value: the guard runs inside a gesture handler and wants the newest
    // index, not the one this render closed over.
    placeIndexRef,
  });

  // Re-date the rest of the trip after this leg's own save landed.
  //
  // A leg's `days` is what dates the NEXT leg, and its `date` is what the ones
  // after it hang off, so either edit moves rows this page doesn't show. Only
  // an actual change counts: buildEditedFields sends a field when the form
  // touched it, which isn't the same as its value differing.
  //
  // Deliberately not gated on saveQueue.isPending: a queued payload can be
  // superseded and never run, and skipping "because another save is coming"
  // would then leave the cascade permanently undone. The chain serializes
  // instead, so the last one to run sees every stored date.
  function cascadeSiblings(saved, payload, previous) {
    const groupKey = tripGroupKey(saved);
    // An unnamed leg is its own group -- nothing to cascade, and no reason to
    // spend a read finding that out.
    if (!isNamedGroupKey(groupKey)) return;

    const daysChanged = payload && 'days' in payload && legDays(payload) !== legDays(previous);
    const dateChanged = payload && 'date' in payload && tripDateMs(payload) !== tripDateMs(previous);
    if (!daysChanged && !dateChanged) return;

    const previousDate = previous?.date;
    return runLegCascade(async () => {
      const legs = await loadGroupLegs(saved.itemId, groupKey);
      // The fetch can still serve the pre-save row (the read goes through a
      // GSI keyed on the very date we just wrote, so it can lag) -- cascade
      // off the copy the mutation returned, which is authoritative.
      const withSaved = legs.map((leg) => (leg.id === saved.id ? saved : leg));

      const index = withSaved.findIndex((leg) => leg.id === saved.id);
      // Not in the group the read came back with (a Trip Name edit racing
      // this one, say). Cascading from index 0 would re-date legs off a leg
      // the user never touched, so do nothing instead.
      if (index < 0) return;

      const { updates } = dateChanged
        ? cascadeAfterDateChange(withSaved, saved.id, { previousDate })
        : cascadeLegDates(withSaved, { fromIndex: index });

      // Never write this leg: its own date is the form's to save, and a
      // hand-built job on a lane a form owns is exactly what the autosave
      // rules forbid. A date edit can only ever produce one for itself when
      // the re-slot logic decides it moved, and that value is already stored.
      await persistLegDates(updates.filter((u) => u.id !== saved.id), saved.itemId);
    }).catch((err) => {
      // Reported, not repaired. persistLegDates can fail PARTWAY -- some
      // siblings take their new date and some don't -- but this page renders
      // only its own leg, so there is no stale list here to correct. The
      // group page is the recovery surface: it re-reads on every visit, and
      // its own cascades reread explicitly on failure (rereadLegs).
      //
      // The page is often GONE by now (a Days edit flushes on the way out), in
      // which case showError is a no-op setState on an unmounted component --
      // which is exactly right: the write still happened, and the destination
      // page re-reads.
      showError(err);
    });
  }

  // Only a NAMED leg can gain a next leg: legs group by Trip Name, so a leg
  // added after an unnamed one would have nothing to share and would stand
  // alone as its own trip. Read off the FORM rather than the stored row so the
  // button appears as soon as a name is typed -- the handler flushes that edit
  // before it creates anything, and re-checks the stored row once it lands.
  // (Same rule as TripGroup's Add leg, which gates on the group key.)
  const canAddNextLeg = Boolean(formState) && isNamedGroupKey(tripGroupKey(formState));

  // Continue the trip from this leg: create the leg that follows it -- starting
  // once this leg's stay is over, departing from wherever it arrived
  // (nextLegDefaults owns those rules, shared with TripGroup's Add leg) -- and
  // open it for editing.
  //
  // This leg need not be the LAST one. A trip's order is its dates, so a new leg
  // dated the day after this one's stay lands directly behind it and the legs
  // that used to follow slide along the days chain -- the same cascade a
  // reorder or a delete runs. On the last leg that cascade is empty.
  async function handleAddNextLeg() {
    if (addingLeg) return;
    setAddingLeg(true);
    try {
      // Everything the new leg inherits -- its date, its Start, the trip name
      // that keeps it in this trip -- comes from the STORED leg, so a debounced
      // edit has to land first. And a cascade this page's own save kicked off
      // has to finish, or the sibling dates read below would be the ones it is
      // still rewriting.
      flush();
      await saveQueue.settled(recordKey);
      await settledLegCascades();

      const current = tripRef.current || trip;
      const groupKey = tripGroupKey(current);
      // The name never reached the server (a failed save, or a create racing
      // this click). Creating anyway would quietly produce a SECOND trip rather
      // than a second leg, so stop and say so.
      if (!isNamedGroupKey(groupKey)) {
        throw new Error('Give this trip a name before adding the next leg.');
      }

      // Read the other legs BEFORE the create, so where the new leg belongs
      // never depends on the trips index having caught up with a row written a
      // moment ago -- nor on a tie-break between two legs sharing a date.
      const siblings = await loadGroupLegs(itemId, groupKey);

      // createTripWithUI reports its own failures and does not await onAdded, so
      // the created row is captured rather than acted on there -- everything
      // below has to run with the button still disabled, and a `null` here is
      // how a failed create is told from a successful one.
      let created = null;
      await Database.createTripWithUI(
        itemId,
        showText,
        showError,
        hideStatus,
        (newTrip) => { created = newTrip; },
        // The id as well as the name: it is what actually keeps the new leg in
        // this trip, and without it a trip whose name another trip shares would
        // gain a leg that reads as belonging to neither.
        nextLegDefaults(current, { name: current.name, tripGroupId: current.tripGroupId })
      );
      if (!created) return;

      // Slide the legs that used to follow this one along the days chain. Its
      // own try/catch, and the navigation happens either way: the leg exists,
      // and leaving the user on the page they just left is worse than a
      // schedule the group page can repair (same reasoning as onConfirmDelete's
      // cascade).
      try {
        // `current` is the authoritative post-save copy of this leg; the read
        // above can still serve the row as it was before that save (the trips
        // index is keyed on the very date we may have just rewritten).
        const withSaved = siblings.map((leg) => (leg.id === current.id ? current : leg));
        const index = withSaved.findIndex((leg) => leg.id === current.id);
        if (index >= 0) {
          const ordered = [
            ...withSaved.slice(0, index + 1),
            created,
            ...withSaved.slice(index + 1),
          ];
          // From THIS leg, so the new one is checked against the chain too --
          // nextLegDefaults already dates it where the cascade would, so it
          // yields no update for itself and only the legs behind it move.
          const { updates } = cascadeLegDates(ordered, { fromIndex: index });
          await persistLegDates(updates, itemId);
        }
      } catch (err) {
        console.error('Added the leg but could not re-date the rest of the trip', err);
      }

      // The same handover every other create path uses. This page IS the
      // destination route, and its load effect (plus the resync block keyed on
      // trip.id) already handles swapping legs without remounting.
      navigate(urlForTrip(itemId, created.id), { state: { item, trip: created } });
    } catch (err) {
      showError(err);
    } finally {
      setAddingLeg(false);
    }
  }

  function deleteTapped() {
    setShowConfirm(true);
  }

  async function onConfirmDelete() {
    try {
      showText('Deleting...');
      setShowConfirm(false);

      // Cancel any pending or in-flight autosave first -- a late update
      // against the deleted row must neither error nor resurrect it.
      saveQueue.cancel(recordKey);

      // Read the trip's other legs BEFORE the row goes, so the gap this leg
      // leaves can be closed. An unnamed leg has none, so it skips the read.
      const groupKey = tripGroupKey(trip);
      const siblings = isNamedGroupKey(groupKey)
        ? await loadGroupLegs(itemId, groupKey)
        : [];

      await Database.deleteTrip(trip.id);

      // Slide the later legs back along the days chain, anchored on the leg
      // BEFORE this one -- deleting the first leg leaves the survivors where
      // they are rather than dragging the trip onto a date nobody chose.
      //
      // Its own try/catch, and the navigation happens either way: leaving the
      // user parked on a page whose trip no longer exists is worse than a
      // schedule they can fix from the group page. (Same reasoning as
      // Database.importTrips' rollback, which logs and re-throws the original.)
      const index = siblings.findIndex((leg) => leg.id === trip.id);
      const remaining = siblings.filter((leg) => leg.id !== trip.id);
      if (index >= 0 && remaining.length > 0) {
        const { updates } = cascadeLegDates(remaining, { fromIndex: Math.max(0, index - 1) });
        try {
          await persistLegDates(updates, itemId);
        } catch (err) {
          console.error('Deleted the leg but could not re-date the rest of the trip', err);
        }
      }

      // That was the trip's last leg, so the trip is gone: its weather email
      // must stop and its share link must be revoked. Its own try/catch, like
      // the re-dating above and for the same reason: the leg is already
      // deleted, and the navigation happens either way. (Both helpers swallow
      // their own failures too; this is the belt to their braces.) The
      // authoritative cleanup for the email is TaskPogoTripWeatherMail's, which
      // removes any subscription whose key no longer resolves to a trip. The
      // share has no equivalent -- the shared page answers "gone" for a trip
      // with no legs, but a legacy 'n:' key is only a name, and a later trip
      // spelled the same way would inherit the link. Same pair, in the same
      // order, as TripGroup's whole-trip Delete.
      if (remaining.length === 0 && isNamedGroupKey(groupKey)) {
        try {
          await removeTripWeatherSubscriptionsForGroup(itemId, groupKey);
          await removeTripShareForGroup(itemId, groupKey);
        } catch (err) {
          console.error('Deleted the trip but could not clean up after it', err);
        }
      }

      // Deleting discards any held edits along with the trip, so don't let
      // the guard prompt about them on the way out (markClean applies
      // synchronously).
      markClean();

      hideStatus();

      // Back to the trip this leg belonged to -- it still exists, and it's
      // where the user was working. An unnamed leg has no group, and a named
      // leg that was the last one just took its trip with it: either way fall
      // back to the trips list rather than an empty group page (the same call
      // TripGroup makes when its last leg goes). Bare navigate: both
      // destinations fetch their own data, and neither reads router state we
      // could hand them. `replace`, so Back doesn't return to this page --
      // the leg it renders is gone.
      const destination = isNamedGroupKey(groupKey) && remaining.length > 0
        ? urlForTripGroup(itemId, groupKey)
        : urlForTrips(itemId);
      navigate(destination, { replace: true });
    } catch (err) {
      setShowConfirm(false);
      showError(err);
    }
  }

  // Export this leg as a single-leg trip file -- the same envelope the group
  // page writes, so it can be re-imported here or as a new trip elsewhere.
  // Exports the persisted trip; unsaved edits stay local until Save.
  async function handleExport() {
    try {
      // Awaited, not read off placeIndexRef: that ref is null until the seed
      // effect lands, so a fast click would silently produce a file with no
      // places section. loadPlaceIndex is cached and never rejects (empty maps
      // on failure just omit the section).
      const placeIndex = await loadPlaceIndex();
      downloadJson(
        suggestedTripFileName(trip.name || tripTitle(displayTrip)),
        serializeTrip([trip], { placeIndex })
      );
    } catch (err) {
      showError(err);
    }
  }

  function pickImportFile() {
    fileInputRef.current?.click();
  }

  // Validate the picked file; a good single-leg file opens the replace
  // confirmation. Whole-trip files belong on the trips list page.
  async function handleImportFile(e) {
    const file = e.target.files?.[0];
    // Reset so choosing the same file again still fires `change`.
    e.target.value = '';
    if (!file) return;
    try {
      const text = await readFileAsText(file);
      const parsed = parseTripExport(text);
      if (parsed.legs.length !== 1) {
        throw new Error(
          `This file contains ${parsed.legs.length} legs. To import a whole trip, use Import JSON on the trips page.`
        );
      }
      hideStatus();
      // Keep the file's places alongside its leg: onConfirmReplace needs both,
      // and the leg alone can't say which gazetteer rows it referred to.
      setPendingReplace({ leg: parsed.legs[0], places: parsed.places });
    } catch (err) {
      showError(err);
    }
  }

  // Confirmed: overwrite this leg's columns and route with the file's. One
  // atomic update; the leg keeps its name (and so its group). Routed through
  // the same save lane so it serializes with any in-flight autosave -- and
  // it REPLACES anything still pending there, which is exactly what the
  // confirmation dialog promised ("unsaved edits will be overwritten").
  async function onConfirmReplace() {
    const parsed = pendingReplace;
    setPendingReplace(null);
    setImporting(true);
    showText('Importing...');

    // The file's places first, so the replaced leg binds to rows that exist
    // HERE rather than to the ids of the environment that wrote it. Create-only
    // and never fatal -- a failure is reported after the leg lands, because the
    // leg is the user's data and the place rows are a bonus.
    //
    // `setImporting(true)` is already set, so the Import/Export buttons stay
    // disabled across this await.
    const { failed, index } = await ensurePlaces(parsed.places);
    const leg = { ...parsed.leg, route: resolveRouteAgainstPlaces(parsed.leg.route, index) };

    saveQueue.schedule(
      recordKey,
      {
        run: () => Database.updateTrip(legToUpdateInput(leg, tripRef.current || trip)),
        onSaved: (saved) => {
          // The import replaces the record wholesale: adopt it into the
          // form/baseline (applyExternalSave merges every saved column) and
          // reset the route draft to the imported route.
          tripRef.current = saved;
          setTrip(saved);
          applyExternalSave(saved);
          const draft = routeDraftFor(saved);
          setRouteDraft(draft);
          routeDraftRef.current = draft;
          routeGen.current += 1;
          persistedRouteGen.current = routeGen.current;
          setImporting(false);
          // This page doesn't navigate on import, so the notice goes straight
          // to its own status line.
          const notice = placesFailedNotice(failed);
          if (notice) showText(notice);
          else hideStatus();
        },
        onError: (err) => {
          setImporting(false);
          showError(err);
        },
        // The lane coalesces to the latest payload. This job carries its own
        // write rather than a rebuild of the form's state, so an edit made
        // while it sits behind an in-flight save would replace it outright --
        // an import the user confirmed would then never run, with the buttons
        // stuck disabled and nothing said. Unwind and ask them to retry.
        onDropped: (reason) => {
          setImporting(false);
          if (reason === 'superseded') {
            showError(
              new Error(
                'Import was interrupted by another edit and did not run. Try importing again.'
              )
            );
          } else {
            hideStatus();
          }
        },
      },
      { delayMs: 0 }
    );
  }

  return (
    <div>
      <ItemBreadcrumbs
        item={item}
        extras={[
          { name: 'Trips', url: urlForTrips(itemId) },
          // A named leg belongs to a group; link back to that group's page.
          ...(trip?.name && String(trip.name).trim() !== ''
            ? [{ name: String(trip.name).trim(), url: urlForTripGroup(itemId, tripGroupKey(trip)) }]
            : []),
        ]}
      />

      <StatusMessage />

      {trip && formState && (
        <div>
          <input
            ref={fileInputRef}
            type="file"
            accept="application/json,.json"
            className="d-none"
            aria-label="Import trip leg JSON file"
            onChange={handleImportFile}
          />

          <DataCard
            header={
              <CardHeader
                text={tripTitle(displayTrip)}
                extraActions={
                  <>
                    <Button variant="outline-secondary" onClick={pickImportFile} disabled={importing}>
                      Import JSON
                    </Button>
                    <Button variant="outline-secondary" onClick={handleExport} disabled={importing}>
                      Export JSON
                    </Button>
                  </>
                }
              />
            }
            body={
              <div>
                {/* Leaving any field flushes the autosave debounce -- onBlur
                    bubbles up from the inputs to this wrapper (it also covers
                    the waypoint list's inputs). */}
                <div onBlur={() => flush()}>
                  <div className="container">
                    {namedHeadFields.map((field, index) => (
                      <Field
                        key={field.key}
                        field={field}
                        state={formState}
                        setState={updateFormState}
                        setFocus={index === 0}
                        inputRef={index === 0 ? firstFieldRef : undefined}
                        error={fieldErrors[field.key]}
                        // These rows sit directly against the waypoint list,
                        // which runs the full width of the card -- `wide`
                        // shares its grid so the two read as one form.
                        layout="wide"
                      />
                    ))}
                  </div>

                  <TripWaypoints
                    route={routeDraft}
                    endpoints={{ from: formState.from, to: formState.to }}
                    locator={locator}
                    onRouteChange={handleRouteChange}
                    onEndpointChange={handleEndpointChange}
                  />

                  <div className="container">
                    {tailFields.map((field) => (
                      <Field
                        key={field.key}
                        field={field}
                        state={formState}
                        setState={updateFormState}
                        error={fieldErrors[field.key]}
                        // Distance is the route's length whenever the route
                        // has one; typing over it would only last until the
                        // next map edit.
                        readOnly={field.key === 'distance' && routeMeasures}
                        note={tailNote(field.key)}
                        layout="wide"
                      />
                    ))}
                  </div>
                </div>

                <div className="container m-2 d-flex justify-content-center align-items-center gap-2 flex-wrap">
                  {/* Disabled while an edit is held invalid or a save has
                      failed: those are exactly the states where the values the
                      new leg inherits aren't the ones that are stored. */}
                  {canAddNextLeg && (
                    <Button
                      variant="outline-secondary"
                      onClick={handleAddNextLeg}
                      disabled={addingLeg || guardDirty}
                    >
                      <i className="fa-solid fa-plus me-1" aria-hidden="true"></i>
                      Add Next Leg
                    </Button>
                  )}
                  {/* An action on THIS leg, so it belongs beside Add Next Leg
                      rather than in the header with Import/Export JSON. Gated
                      like Add Next Leg and for the same reason: a held-invalid
                      edit or a failed save is exactly the state where what
                      would be copied isn't what is stored. */}
                  <Button
                    variant="outline-secondary"
                    onClick={() => copyLegToLogbook(trip)}
                    disabled={addingLeg || guardDirty || copying}
                  >
                    <i className="fa-solid fa-book me-1" aria-hidden="true"></i>
                    Copy to Captain's Log
                  </Button>
                  <Button variant="danger" onClick={deleteTapped} disabled={addingLeg}>
                    Delete
                  </Button>
                </div>
              </div>
            }
          />

          {/* The chunk renders its own cards: map, route summary, weather
              details. Edits flow back here so the page's autosave (and the
              unsaved-changes guard) owns persistence -- including Distance,
              which handleRouteChange recomputes from the route. */}
          <Suspense fallback={<Spinner />}>
            <TripRouteMap
              trip={formState}
              route={routeDraft}
              locator={locator}
              onRouteChange={handleRouteChange}
              placeIndex={placeIndex}
              onEditPlace={handleEditPlace}
              onPlaceVisits={handlePlaceVisits}
            />
          </Suspense>
        </div>
      )}

      <Confirm
        show={showConfirm}
        onConfirm={onConfirmDelete}
        onHide={() => setShowConfirm(false)}
        body='Are you sure?'
        confirmText='Confirm Delete'
        title='Delete Trip'
      />

      <Confirm
        show={Boolean(pendingReplace)}
        onConfirm={onConfirmReplace}
        onHide={() => setPendingReplace(null)}
        body="Replace this leg's date, details, and route with the imported leg? Current values (and any unsaved edits) will be overwritten. The trip name is kept."
        confirmText='Replace'
        title='Import Trip Leg'
      />

      <Confirm
        show={showLeavePrompt}
        onConfirm={confirmLeave}
        onHide={cancelLeave}
        body="Some changes couldn't be saved. Leave and discard them?"
        cancelText='Stay'
        confirmText='Discard Changes'
        title='Unsaved Changes'
      />

      {locator.nearbyModal}

      {copyToLogModals}
    </div>
  );
};

export default Trip;
